import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView } from 'framer-motion';
import SectionHeading from '../SectionHeading';
import { cardHover, fadeUp, stagger, viewport } from '../motion';
import Hackerrenk from "../SVG/Hackerrenk.png";
import GFG from "../SVG/gfg.jpeg";
import LinkedIn from "../SVG/link.png";
import LeetCode from "../SVG/leet.png";
import GitHub from "../SVG/git.png";

// Each platform carries its own brand accent rather than one shared blue —
// the colour is how you recognise the profile before you read the name.
const practice = [
  {
    name: "GeeksforGeeks",
    handle: "mayur_adlak",
    focus: "Ranked #1 across my university, 792 coding score.",
    img: GFG,
    url: "https://www.geeksforgeeks.org/profile/mayur_adlak?tab=activity",
    accent: "#2F8D46",
    tint: "rgba(47, 141, 70, 0.08)",
  },
  {
    name: "LeetCode",
    handle: "Mayuradlak123",
    focus: "Data structures, algorithms and daily contest problems.",
    img: LeetCode,
    url: "https://leetcode.com/Mayuradlak123/",
    accent: "#E8890C",
    tint: "rgba(255, 161, 22, 0.10)",
  },
  {
    name: "HackerRank",
    handle: "mayuradlak030",
    focus: "Problem solving, SQL and language proficiency tracks.",
    img: Hackerrenk,
    url: "https://www.hackerrank.com/mayuradlak030",
    accent: "#00A550",
    tint: "rgba(0, 165, 80, 0.08)",
  },
];

const social = [
  {
    name: "GitHub",
    handle: "Mayuradlak123",
    focus: "Source for the projects below, plus experiments in progress.",
    img: GitHub,
    url: "https://www.github.com/Mayuradlak123",
    accent: "#24292F",
    tint: "rgba(36, 41, 47, 0.06)",
  },
  {
    name: "LinkedIn",
    handle: "mayur-adlak",
    focus: "Work history, recommendations and what I'm building now.",
    img: LinkedIn,
    url: "https://www.linkedin.com/in/mayur-adlak-31b962220/",
    accent: "#0A66C2",
    tint: "rgba(10, 102, 194, 0.08)",
  },
];

// Split so the number can count up while the prefix/suffix stay put.
const stats = [
  { prefix: "", value: 300, suffix: "+", label: "Problems solved" },
  { prefix: "", value: 792, suffix: "", label: "GFG coding score" },
  { prefix: "#", value: 1, suffix: "", label: "University rank" },
];

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      setDisplay(value);
      return undefined;
    }

    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setDisplay(Math.round(latest)),
    });

    return () => controls.stop();
  }, [inView, value]);

  return <span ref={ref}>{display}</span>;
}

function PlatformCard({ platform }) {
  return (
    <motion.a
      href={platform.url}
      target="_blank"
      rel="noreferrer"
      aria-label={`${platform.name} profile, opens in a new tab`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-l-4 border-slate-200/80 bg-white p-5 shadow-sm transition-shadow duration-300 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
      style={{ borderLeftColor: platform.accent }}
      variants={fadeUp}
      whileHover={cardHover}
    >
      {/* Brand wash, top-right, on hover only */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
        style={{ backgroundColor: platform.accent }}
      ></span>

      <div className="relative flex items-start gap-4">
        {/* White chip keeps the mixed PNG/JPEG logos from clashing with each other */}
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-2 shadow-sm ring-1 ring-slate-200 transition-transform duration-300 group-hover:scale-105">
          <img
            src={platform.img}
            alt=""
            aria-hidden="true"
            className="max-h-full max-w-full object-contain"
          />
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="truncate text-base font-bold leading-tight text-slate-800">
            {platform.name}
          </h4>
          <span className="mt-0.5 block truncate font-mono text-[11px] text-slate-400">
            @{platform.handle}
          </span>
        </div>

        <svg
          className="h-4 w-4 shrink-0 text-slate-300 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 17L17 7M7 7h10v10" />
        </svg>
      </div>

      <p className="relative mt-4 flex-1 text-sm leading-relaxed text-slate-600">
        {platform.focus}
      </p>

      <div className="relative mt-5 flex items-center gap-3 border-t border-slate-100 pt-3">
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: platform.accent }}
        ></span>
        <span
          className="text-[11px] tracking-wide"
          style={{ color: platform.accent }}
        >
          View profile
        </span>
      </div>
    </motion.a>
  );
}

function GroupHeading({ children, count }) {
  return (
    <div className="mb-6 flex items-center gap-4">
      <h3 className="whitespace-nowrap text-lg font-bold tracking-tight text-slate-800 sm:text-xl">
        {children}
      </h3>
      <span className="font-mono text-xs tabular-nums text-slate-400">
        {String(count).padStart(2, '0')}
      </span>
      <span className="h-px flex-1 bg-gradient-to-r from-slate-300 to-transparent"></span>
    </div>
  );
}

function Coding() {
  return (
    <section id="coding" className="relative overflow-hidden py-20">
      {/* Decorative elements */}
      <div className="absolute left-10 top-20 h-36 w-36 animate-blob-slow rounded-full bg-purple-200 opacity-20 mix-blend-multiply blur-xl"></div>
      <div className="absolute bottom-10 right-10 h-32 w-32 animate-blob-medium rounded-full bg-blue-200 opacity-20 mix-blend-multiply blur-xl"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Practice" title="Coding Profile" className="mb-12">
          Where I practise, compete and publish code. Consistent problem solving is what keeps my
          fundamentals sharp between shipping features.
        </SectionHeading>

        {/* Stat strip */}
        <motion.dl
          className="mx-auto mb-14 grid max-w-3xl grid-cols-3 divide-x divide-slate-200 overflow-hidden rounded-2xl border border-slate-200/80 bg-white/80 shadow-sm backdrop-blur"
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={fadeUp}
              className="flex flex-col px-4 py-5 text-center sm:px-6"
            >
              <dt className="order-2 mt-1 text-[10px] tracking-wide text-slate-500 sm:text-[11px]">
                {stat.label}
              </dt>
              <dd className="order-1 bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-2xl font-bold tabular-nums text-transparent sm:text-3xl">
                {stat.prefix}
                <CountUp value={stat.value} />
                {stat.suffix}
              </dd>
            </motion.div>
          ))}
        </motion.dl>

        <div className="mb-14">
          <GroupHeading count={practice.length}>Practice &amp; Competitive</GroupHeading>
          <motion.div
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            variants={stagger(0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            {practice.map((platform) => (
              <PlatformCard key={platform.name} platform={platform} />
            ))}
          </motion.div>
        </div>

        <div>
          <GroupHeading count={social.length}>Code &amp; Network</GroupHeading>
          <motion.div
            className="grid grid-cols-1 gap-6 sm:grid-cols-2"
            variants={stagger(0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            {social.map((platform) => (
              <PlatformCard key={platform.name} platform={platform} />
            ))}
          </motion.div>
        </div>

        <motion.div
          className="mt-14 text-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
        >
          <a
            href="#contact"
            className="group inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-3 font-medium text-white shadow-md transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            Let's Connect
            <svg
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7-7 7M3 12h18" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

export default Coding;
